import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal } from 'lucide-react';
import api from '../lib/api';
import { formatPrice } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Local slider state for smooth UI
  const [localMinCarat, setLocalMinCarat] = useState(0);
  const [localMaxCarat, setLocalMaxCarat] = useState(100);
  const [localMinPrice, setLocalMinPrice] = useState(0);
  const [localMaxPrice, setLocalMaxPrice] = useState(1000000);

  const [filters, setFilters] = useState({
    category: searchParams.get('category') || 'all',
    gem_type: searchParams.get('gem_type') || 'all',
    shape: searchParams.get('shape') || 'all',
    min_carat: parseFloat(searchParams.get('min_carat')) || 0,
    max_carat: parseFloat(searchParams.get('max_carat')) || 100,
    min_price: parseFloat(searchParams.get('min_price')) || 0,
    max_price: parseFloat(searchParams.get('max_price')) || 1000000,
    origin: searchParams.get('origin') || 'all',
    treatment: searchParams.get('treatment') || 'all',
    certification_lab: searchParams.get('certification_lab') || 'all',
    stock_status: searchParams.get('stock_status') || 'all',
    sort_by: searchParams.get('sort_by') || 'created_at',
    sort_order: searchParams.get('sort_order') || 'desc',
  });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [filters, searchTerm]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all' && value !== 0) params.append(key, value);
      });
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await api.get(`/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      category: 'all',
      gem_type: 'all',
      shape: 'all',
      min_carat: 0,
      max_carat: 100,
      min_price: 0,
      max_price: 1000000,
      origin: 'all',
      treatment: 'all',
      certification_lab: 'all',
      stock_status: 'all',
      sort_by: 'created_at',
      sort_order: 'desc',
    });
    setLocalMinCarat(0);
    setLocalMaxCarat(100);
    setLocalMinPrice(0);
    setLocalMaxPrice(1000000);
    setSearchTerm('');
  };

  const FilterSection = () => (
    <div className="space-y-6">
      {/* Category */}
      <div>
        <label className="block text-sm font-medium mb-2">Category</label>
        <Select value={filters.category} onValueChange={(value) => updateFilter('category', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="gemstone">Loose Gemstones</SelectItem>
            <SelectItem value="jewellery">Jewellery</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Gem Type */}
      <div>
        <label className="block text-sm font-medium mb-2">Gemstone Type</label>
        <Select value={filters.gem_type} onValueChange={(value) => updateFilter('gem_type', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="emerald">Emerald</SelectItem>
            <SelectItem value="ruby">Ruby</SelectItem>
            <SelectItem value="sapphire">Sapphire</SelectItem>
            <SelectItem value="other">Other Gemstones</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Shape */}
      <div>
        <label className="block text-sm font-medium mb-2">Shape</label>
        <Select value={filters.shape} onValueChange={(value) => updateFilter('shape', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Shapes</SelectItem>
            <SelectItem value="Oval">Oval</SelectItem>
            <SelectItem value="Cushion">Cushion</SelectItem>
            <SelectItem value="Emerald">Emerald Cut</SelectItem>
            <SelectItem value="Round">Round</SelectItem>
            <SelectItem value="Pear">Pear</SelectItem>
            <SelectItem value="Heart">Heart</SelectItem>
            <SelectItem value="Marquise">Marquise</SelectItem>
            <SelectItem value="Princess">Princess</SelectItem>
            <SelectItem value="Cabochon">Cabochon</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Carat Range */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Carat Weight: {localMinCarat.toFixed(1)} - {localMaxCarat.toFixed(1)} ct
        </label>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-600">Min Carat</label>
            <Slider
              value={[localMinCarat]}
              onValueChange={(value) => setLocalMinCarat(value[0])}
              onValueCommit={(value) => updateFilter('min_carat', value[0])}
              max={100}
              step={0.5}
              className="mt-2"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Max Carat</label>
            <Slider
              value={[localMaxCarat]}
              onValueChange={(value) => setLocalMaxCarat(value[0])}
              onValueCommit={(value) => updateFilter('max_carat', value[0])}
              max={100}
              step={0.5}
              className="mt-2"
            />
          </div>
        </div>
      </div>

      {/* Price Range */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Price: ${localMinPrice.toLocaleString()} - ${localMaxPrice.toLocaleString()}
        </label>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-600">Min Price</label>
            <Slider
              value={[localMinPrice]}
              onValueChange={(value) => setLocalMinPrice(value[0])}
              onValueCommit={(value) => updateFilter('min_price', value[0])}
              max={1000000}
              step={10000}
              className="mt-2"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Max Price</label>
            <Slider
              value={[localMaxPrice]}
              onValueChange={(value) => setLocalMaxPrice(value[0])}
              onValueCommit={(value) => updateFilter('max_price', value[0])}
              max={1000000}
              step={10000}
              className="mt-2"
            />
          </div>
        </div>
      </div>

      {/* Origin */}
      <div>
        <label className="block text-sm font-medium mb-2">Origin</label>
        <Select value={filters.origin} onValueChange={(value) => updateFilter('origin', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Origins</SelectItem>
            <SelectItem value="Colombia">Colombia</SelectItem>
            <SelectItem value="Myanmar">Myanmar (Burma)</SelectItem>
            <SelectItem value="Kashmir">Kashmir</SelectItem>
            <SelectItem value="Zambia">Zambia</SelectItem>
            <SelectItem value="Sri Lanka">Sri Lanka (Ceylon)</SelectItem>
            <SelectItem value="Mozambique">Mozambique</SelectItem>
            <SelectItem value="Afghanistan">Afghanistan</SelectItem>
            <SelectItem value="Thailand">Thailand</SelectItem>
            <SelectItem value="Madagascar">Madagascar</SelectItem>
            <SelectItem value="Brazil">Brazil</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Treatment */}
      <div>
        <label className="block text-sm font-medium mb-2">Treatment</label>
        <Select value={filters.treatment} onValueChange={(value) => updateFilter('treatment', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Treatments</SelectItem>
            <SelectItem value="Untreated">Untreated</SelectItem>
            <SelectItem value="Unheated">Unheated</SelectItem>
            <SelectItem value="Heated">Heated</SelectItem>
            <SelectItem value="Minor oil">Minor Oil</SelectItem>
            <SelectItem value="Moderate oil">Moderate Oil</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Certification Lab */}
      <div>
        <label className="block text-sm font-medium mb-2">Certification</label>
        <Select value={filters.certification_lab} onValueChange={(value) => updateFilter('certification_lab', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Certifications</SelectItem>
            <SelectItem value="GIA">GIA</SelectItem>
            <SelectItem value="GRS">GRS</SelectItem>
            <SelectItem value="Gübelin">Gübelin</SelectItem>
            <SelectItem value="SSEF">SSEF</SelectItem>
            <SelectItem value="AGL">AGL</SelectItem>
            <SelectItem value="Lotus">Lotus Gemology</SelectItem>
            <SelectItem value="None">No Certificate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stock Status */}
      <div>
        <label className="block text-sm font-medium mb-2">Availability</label>
        <Select value={filters.stock_status} onValueChange={(value) => updateFilter('stock_status', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="in_stock">In Stock</SelectItem>
            <SelectItem value="reserved">Reserved</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button onClick={resetFilters} variant="outline" className="w-full">
        Reset Filters
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-4xl md:text-5xl mb-2">Shop Gemstones</h1>
          <p className="text-gray-600">Discover our collection of certified loose gemstones</p>
        </div>

        {/* Search & Sort Bar */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search gemstones by name, color, origin..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>

          {/* Mobile Filter Button */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="md:hidden">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Filter Gemstones</SheetTitle>
                <SheetDescription>Refine your search</SheetDescription>
              </SheetHeader>
              <div className="mt-6 pb-20">
                <FilterSection />
              </div>
            </SheetContent>
          </Sheet>

          {/* Sort */}
          <Select 
            value={`${filters.sort_by}_${filters.sort_order}`} 
            onValueChange={(value) => {
              const [sortBy, sortOrder] = value.split('_');
              setFilters(prev => ({ ...prev, sort_by: sortBy, sort_order: sortOrder }));
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at_desc">Newest First</SelectItem>
              <SelectItem value="created_at_asc">Oldest First</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
              <SelectItem value="carat_weight_asc">Carat: Low to High</SelectItem>
              <SelectItem value="carat_weight_desc">Carat: High to Low</SelectItem>
              <SelectItem value="name_asc">Name: A to Z</SelectItem>
              <SelectItem value="name_desc">Name: Z to A</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <div className="sticky top-24 border border-gray-200 p-6 max-h-[calc(100vh-120px)] overflow-y-auto">
              <h2 className="font-serif text-xl mb-4">Filters</h2>
              <FilterSection />
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            <div className="mb-4 text-sm text-gray-600">
              {loading ? 'Loading...' : `${products.length} gemstones found`}
            </div>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-200 aspect-square mb-2"></div>
                    <div className="bg-gray-200 h-4 mb-1"></div>
                    <div className="bg-gray-200 h-3 w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-600">No gemstones found matching your criteria</p>
                <Button onClick={resetFilters} variant="outline" className="mt-4">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.02 }}
                    onClick={() => navigate(`/products/${product.id}`)}
                    className="group cursor-pointer border border-gray-200 hover:border-black transition-all"
                    data-testid={`product-card-${index}`}
                  >
                    {/* Image */}
                    <div className="product-image-wrapper aspect-square bg-gray-50 relative">
                      <img
                        src={product.main_image_url || 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=400&q=80'}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=400&q=80';
                        }}
                      />
                      {/* SOLD Overlay */}
                      {product.stock_status === 'sold' && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <div className="bg-white text-black px-4 py-2 font-bold text-lg tracking-wider">
                            SOLD
                          </div>
                        </div>
                      )}
                      {/* Reserved Badge */}
                      {product.stock_status === 'reserved' && (
                        <div className="absolute top-2 right-2 bg-yellow-500 text-black px-2 py-1 text-[10px] font-bold">
                          RESERVED
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-3 space-y-1">
                      <h3 className="text-xs font-medium line-clamp-2 group-hover:text-gray-600 transition-colors">
                        {product.name}
                      </h3>
                      
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>{product.carat_weight} ct</span>
                        <span className="uppercase text-[10px]">{product.gem_type}</span>
                      </div>
                      
                      <p className="text-xs text-gray-500 line-clamp-1">
                        {product.origin}
                      </p>
                      
                      <p className="text-sm font-semibold pt-1">
                        {product.price_on_request ? 'POA' : (
                          product.stock_status === 'sold' ? (
                            <span className="line-through text-gray-400">{formatPrice(product.price, product.currency)}</span>
                          ) : (
                            formatPrice(product.price, product.currency)
                          )
                        )}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
